import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { BuscadorCliente, nombreCliente } from '../../components/comercial/BuscadorCliente';
import { LoadingState, EmptyState } from '../../components/comercial/shared';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Plus, Download, MessageCircle, Mail, Trash2, Home, Building2, PenLine, Zap, X, Link2, User } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_STYLES = {
  borrador: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
  pendiente_firma: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  pendiente_activar_suscripcion: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  suscripcion_activa: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  cancelado: 'bg-zinc-700/30 text-zinc-500 border-zinc-600/30',
};

const EMPTY_FORM = {
  trial_id: null,
  cliente_nombre: '',
  cliente_telefono: '',
  cliente_email: '',
  tipo_cliente: 'hogar',
  equipo_id: '',
  tarifa_precio: '',
  tarifa_original: null,
  fianza_importe: '',
  fecha_inicio_suscripcion: '',
  accesorios: [],
  observaciones: '',
};

export default function PresupuestosPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [presupuestos, setPresupuestos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [clienteSel, setClienteSel] = useState(null);
  const [vinculando, setVinculando] = useState(null); // presupuesto sin cliente que se está vinculando
  const [nuevoAccesorio, setNuevoAccesorio] = useState({ nombre: '', precio: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [presRes, eqRes] = await Promise.all([
        axios.get(`${API_URL}/api/comercial/presupuestos`, { withCredentials: true }),
        axios.get(`${API_URL}/api/comercial/equipos`, { withCredentials: true }),
      ]);
      setPresupuestos(presRes.data.presupuestos || []);
      setEquipos(eqRes.data || []);
    } catch (err) {
      console.error('Error fetching presupuestos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleEquipoChange = async (equipoId) => {
    setForm((f) => ({ ...f, equipo_id: equipoId }));
    if (!equipoId) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/comercial/tarifa-por-equipo/${equipoId}`,
        { params: { tipo_cliente: form.tipo_cliente }, withCredentials: true }
      );
      const tarifa = response.data.tarifa;
      if (tarifa?.precio) {
        setForm((f) => ({ ...f, equipo_id: equipoId, tarifa_precio: tarifa.precio, tarifa_original: tarifa.precio }));
      } else {
        setForm((f) => ({ ...f, equipo_id: equipoId, tarifa_original: null }));
        toast.info('Tarifa pendiente de definir para este equipo. Introduce el precio manualmente.');
      }
    } catch (err) {
      console.error('Error fetching tarifa:', err);
    }
  };

  const addAccesorio = () => {
    if (!nuevoAccesorio.nombre || !nuevoAccesorio.precio) return;
    setForm((f) => ({
      ...f,
      accesorios: [...f.accesorios, { nombre: nuevoAccesorio.nombre, precio: parseFloat(nuevoAccesorio.precio) }],
    }));
    setNuevoAccesorio({ nombre: '', precio: '' });
  };

  // Cliente del CRM: rellena sus datos y vincula el presupuesto a su ficha
  const handleSeleccionarCliente = (c) => {
    const tipo = c.tipo_cliente === 'empresa' ? 'empresa' : 'hogar';
    setClienteSel(c);
    setForm((f) => ({
      ...f,
      trial_id: c.id,
      cliente_nombre: nombreCliente(c),
      cliente_telefono: c.telefono || '',
      cliente_email: c.email || '',
      tipo_cliente: tipo,
      // La tarifa depende del tipo de cliente
      ...(tipo !== f.tipo_cliente ? { equipo_id: '', tarifa_precio: '', tarifa_original: null } : {}),
    }));
  };

  const handleQuitarCliente = () => {
    setClienteSel(null);
    setForm((f) => ({ ...f, trial_id: null }));
  };

  const cerrarModal = (open) => {
    setShowModal(open);
    if (!open) {
      setForm(EMPTY_FORM);
      setClienteSel(null);
    }
  };

  const handleVincular = async (cliente) => {
    try {
      await axios.post(
        `${API_URL}/api/comercial/presupuestos/${vinculando.id}/vincular-cliente`,
        { trial_id: cliente.id },
        { withCredentials: true }
      );
      toast.success(`Presupuesto vinculado a ${nombreCliente(cliente)}`);
      setVinculando(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al vincular el cliente');
    }
  };

  const removeAccesorio = (index) => {
    setForm((f) => ({ ...f, accesorios: f.accesorios.filter((_, i) => i !== index) }));
  };

  const handleCreate = async () => {
    if (!form.cliente_nombre || !form.equipo_id || !form.tarifa_precio) {
      toast.error('Cliente, equipo y tarifa son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const equipo = equipos.find((e) => e.id === form.equipo_id);
      await axios.post(`${API_URL}/api/comercial/presupuestos`, {
        ...form,
        equipo_nombre: equipo?.nombre || form.equipo_id,
        tarifa_precio: parseFloat(form.tarifa_precio),
        fianza_importe: form.fianza_importe ? parseFloat(form.fianza_importe) : null,
        fecha_inicio_suscripcion: form.fecha_inicio_suscripcion || null,
      }, { withCredentials: true });
      toast.success('Presupuesto generado correctamente');
      cerrarModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar el presupuesto');
    } finally {
      setSaving(false);
    }
  };

  const handleVerPdf = (p) => {
    window.open(`${API_URL}/api/comercial/presupuestos/${p.id}/pdf`, '_blank');
  };

  const handleEnviar = async (p, canal) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/comercial/presupuestos/${p.id}/enviar`,
        { canal },
        { withCredentials: true }
      );
      if (canal === 'whatsapp' && response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    }
  };

  const handleAccion = async (p, accion, mensajeOk) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/comercial/presupuestos/${p.id}/${accion}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message || mensajeOk);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la operación');
    }
  };

  return (
    <ComercialLayout
      title="Presupuestos"
      subtitle={`${presupuestos.length} presupuestos generados`}
      onLogout={handleLogout}
      headerActions={
        <Button
          onClick={() => setShowModal(true)}
          className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
          data-testid="presupuestos-nuevo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Presupuesto
        </Button>
      }
    >
      <div className="space-y-4" data-testid="presupuestos-page">
        {loading ? (
          <LoadingState />
        ) : presupuestos.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Sin presupuestos"
            description="Genera tu primer presupuesto con el botón 'Nuevo Presupuesto'."
          />
        ) : (
          <div className="space-y-3">
            {presupuestos.map((p) => (
              <div
                key={p.id}
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]"
                data-testid={`presupuesto-card-${p.numero}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-zinc-500">{p.numero}</span>
                      <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${ESTADO_STYLES[p.estado] || ESTADO_STYLES.borrador}`} data-testid={`presupuesto-estado-${p.numero}`}>
                        {p.estado_label || p.estado}
                      </span>
                    </div>
                    <p className="text-sm text-white font-medium">{p.cliente_nombre}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {p.equipo_nombre} · {p.tarifa_precio?.toFixed(2)} €/mes
                      {p.tipo_cliente === 'hogar' ? ' (IVA incl.)' : ' (+IVA)'}
                      {p.fianza_importe ? ` · Fianza ${p.fianza_importe.toFixed(2)} €` : ''}
                      {p.accesorios?.length > 0 ? ` · ${p.accesorios.length} accesorio(s)` : ''}
                    </p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Válido hasta {p.valido_hasta}
                      {p.fecha_inicio_suscripcion ? ` · Inicio suscripción: ${p.fecha_inicio_suscripcion}` : ''}
                    </p>
                    {p.trial_id ? (
                      <Link
                        to={`/comercial/cliente/${p.trial_id}`}
                        className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 mt-1"
                        data-testid={`presupuesto-ficha-${p.numero}`}
                      >
                        <User className="w-3 h-3" />
                        Ver ficha del cliente
                      </Link>
                    ) : p.estado !== 'cancelado' && (
                      <p className="text-xs text-amber-400 mt-1">Sin cliente vinculado</p>
                    )}
                    {p.estado === 'suscripcion_activa' && p.suscripcion && (
                      <p className="text-xs text-emerald-400 mt-1">
                        ✓ Suscripción activa desde {p.suscripcion.fecha_inicio} · {p.suscripcion.cuota_mensual?.toFixed(2)} €/mes
                        {p.suscripcion.odoo_subscription_id ? '' : ' (pendiente sincronizar con Odoo)'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleVerPdf(p)}
                      className="border-white/20 text-zinc-300 hover:text-white"
                      data-testid={`presupuesto-pdf-${p.numero}`}
                    >
                      <Download className="w-4 h-4 mr-1.5" />
                      PDF
                    </Button>
                    {['borrador', 'pendiente_firma'].includes(p.estado) && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleEnviar(p, 'whatsapp')}
                          className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                          data-testid={`presupuesto-whatsapp-${p.numero}`}
                        >
                          <MessageCircle className="w-4 h-4 mr-1.5" />
                          WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEnviar(p, 'email')}
                          className="bg-sky-500/20 text-sky-400 border border-sky-500/30 hover:bg-sky-500/30"
                          data-testid={`presupuesto-email-${p.numero}`}
                        >
                          <Mail className="w-4 h-4 mr-1.5" />
                          Email
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAccion(p, 'marcar-firmado', 'Presupuesto firmado')}
                          className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
                          data-testid={`presupuesto-firmar-${p.numero}`}
                        >
                          <PenLine className="w-4 h-4 mr-1.5" />
                          Marcar firmado
                        </Button>
                      </>
                    )}
                    {!p.trial_id && !['suscripcion_activa', 'cancelado'].includes(p.estado) && (
                      <Button
                        size="sm"
                        variant={p.estado === 'pendiente_activar_suscripcion' ? 'default' : 'outline'}
                        onClick={() => setVinculando(p)}
                        className={p.estado === 'pendiente_activar_suscripcion'
                          ? 'bg-amber-500 text-black hover:bg-amber-400'
                          : 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'}
                        title="Necesario para activar la suscripción y crear su acceso al Portal Cliente"
                        data-testid={`presupuesto-vincular-${p.numero}`}
                      >
                        <Link2 className="w-4 h-4 mr-1.5" />
                        Vincular cliente
                      </Button>
                    )}
                    {p.estado === 'pendiente_activar_suscripcion' && p.trial_id && (
                      <Button
                        size="sm"
                        onClick={() => handleAccion(p, 'activar-suscripcion', 'Suscripción activada')}
                        className="bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20"
                        data-testid={`presupuesto-activar-${p.numero}`}
                      >
                        <Zap className="w-4 h-4 mr-1.5" />
                        Activar suscripción
                      </Button>
                    )}
                    {!['suscripcion_activa', 'cancelado'].includes(p.estado) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAccion(p, 'cancelar', 'Presupuesto cancelado')}
                        className="border-white/10 text-zinc-500 hover:text-red-400 hover:border-red-500/30"
                        data-testid={`presupuesto-cancelar-${p.numero}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Nuevo Presupuesto */}
      <Dialog open={showModal} onOpenChange={cerrarModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] font-light text-xl">Nuevo Presupuesto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Cliente del CRM */}
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Cliente del CRM</label>
              <BuscadorCliente
                seleccionado={clienteSel}
                onSeleccionar={handleSeleccionarCliente}
                onQuitar={handleQuitarCliente}
              />
              {!clienteSel && (
                <p className="text-[10px] text-zinc-600 mt-1.5">
                  Opcional para un presupuesto rápido, pero hará falta vincularlo para activar la suscripción.
                </p>
              )}
            </div>

            {/* Tipo cliente */}
            <div className="grid grid-cols-2 gap-2">
              {[{ id: 'hogar', label: 'Hogar', icon: Home }, { id: 'empresa', label: 'Empresa', icon: Building2 }].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setForm((f) => ({ ...f, tipo_cliente: t.id, tarifa_precio: '', tarifa_original: null, equipo_id: '' }))}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-colors ${
                    form.tipo_cliente === t.id
                      ? 'bg-white/10 border-white/30 text-white'
                      : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`presupuesto-tipo-${t.id}`}
                >
                  <t.icon className="w-4 h-4" />
                  <span className="text-sm">{t.label}</span>
                </button>
              ))}
            </div>

            {/* Datos cliente */}
            <div className="space-y-3">
              <Input
                value={form.cliente_nombre}
                onChange={(e) => setForm((f) => ({ ...f, cliente_nombre: e.target.value }))}
                placeholder="Nombre del cliente *"
                className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                data-testid="presupuesto-cliente-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  value={form.cliente_telefono}
                  onChange={(e) => setForm((f) => ({ ...f, cliente_telefono: e.target.value }))}
                  placeholder="Teléfono"
                  className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="presupuesto-telefono-input"
                />
                <Input
                  value={form.cliente_email}
                  onChange={(e) => setForm((f) => ({ ...f, cliente_email: e.target.value }))}
                  placeholder="Email"
                  className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="presupuesto-email-input"
                />
              </div>
            </div>

            {/* Equipo */}
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Equipo *</label>
              <select
                value={form.equipo_id}
                onChange={(e) => handleEquipoChange(e.target.value)}
                className="w-full p-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm"
                data-testid="presupuesto-equipo-select"
              >
                <option value="" className="bg-zinc-900">Seleccionar equipo</option>
                {equipos.map((eq) => (
                  <option key={eq.id} value={eq.id} className="bg-zinc-900">
                    {eq.nombre}{eq.capacidad ? ` (${eq.capacidad})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Tarifa y fianza */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">
                  Tarifa €/mes * {form.tipo_cliente === 'hogar' ? '(IVA incl.)' : '(+IVA)'}
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.tarifa_precio}
                  onChange={(e) => setForm((f) => ({ ...f, tarifa_precio: e.target.value }))}
                  placeholder="0.00"
                  className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="presupuesto-tarifa-input"
                />
                {form.tarifa_original && parseFloat(form.tarifa_precio) !== form.tarifa_original && (
                  <p className="text-[10px] text-amber-400 mt-1">Oferta especial (tarifa oficial: {form.tarifa_original.toFixed(2)} €)</p>
                )}
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Fianza retornable €</label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.fianza_importe}
                  onChange={(e) => setForm((f) => ({ ...f, fianza_importe: e.target.value }))}
                  placeholder="0.00"
                  className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="presupuesto-fianza-input"
                />
              </div>
            </div>

            {/* Fecha de inicio de la suscripción */}
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Fecha de inicio de la suscripción (se enviará a Odoo)</label>
              <input
                type="date"
                value={form.fecha_inicio_suscripcion}
                onChange={(e) => setForm((f) => ({ ...f, fecha_inicio_suscripcion: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm [color-scheme:dark]"
                data-testid="presupuesto-fecha-inicio-input"
              />
            </div>

            {/* Accesorios */}
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Accesorios</label>
              {form.accesorios.map((a, i) => (
                <div key={i} className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-white/[0.03] border border-white/10">
                  <span className="flex-1 text-sm text-zinc-300">{a.nombre}</span>
                  <span className="text-sm text-zinc-400">{a.precio.toFixed(2)} €</span>
                  <button onClick={() => removeAccesorio(i)} className="text-zinc-500 hover:text-red-400" data-testid={`presupuesto-accesorio-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={nuevoAccesorio.nombre}
                  onChange={(e) => setNuevoAccesorio((a) => ({ ...a, nombre: e.target.value }))}
                  placeholder="Nombre del accesorio"
                  className="flex-1 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="presupuesto-accesorio-nombre"
                />
                <Input
                  type="number"
                  step="0.01"
                  value={nuevoAccesorio.precio}
                  onChange={(e) => setNuevoAccesorio((a) => ({ ...a, precio: e.target.value }))}
                  placeholder="€"
                  className="w-24 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="presupuesto-accesorio-precio"
                />
                <Button
                  variant="outline"
                  onClick={addAccesorio}
                  className="border-white/20 text-zinc-300"
                  data-testid="presupuesto-accesorio-add"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Observaciones */}
            <Textarea
              value={form.observaciones}
              onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))}
              placeholder="Observaciones (opcional)"
              className="bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 min-h-[70px]"
              data-testid="presupuesto-observaciones"
            />

            <Button
              onClick={handleCreate}
              disabled={saving}
              className="w-full bg-white text-black hover:bg-zinc-200"
              data-testid="presupuesto-generar-btn"
            >
              {saving ? 'Generando...' : 'Generar Presupuesto'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vincular cliente a un presupuesto existente */}
      <Dialog open={!!vinculando} onOpenChange={(open) => !open && setVinculando(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] font-light text-xl">Vincular cliente</DialogTitle>
          </DialogHeader>
          {vinculando && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">
                {vinculando.numero} · {vinculando.cliente_nombre}. Elige su ficha del CRM: al activar la suscripción
                se le creará el acceso al Portal Cliente con el email de la ficha.
              </p>
              <BuscadorCliente onSeleccionar={handleVincular} autoFocus />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ComercialLayout>
  );
}
